import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addEmployeeExperience,
  updateEmployeeExperience,
  deleteEmployeeExperience,
  employeeKeys,
} from '../services/employeeApiService';
import type { AddEmployeeExperienceDto, UpdateEmployeeExperienceDto } from '../services/employeeApiService';

interface ExperienceMutationOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for adding a past-experience record. On success, invalidates
 * the employee-details query so ProfilePage's experience list refetches from
 * the server instead of being patched locally.
 */
export function useAddExperience(employeeId: string, tenantId: string, options: ExperienceMutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, Error, AddEmployeeExperienceDto>({
    mutationFn: (data) => addEmployeeExperience(employeeId, tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId, tenantId) });
      options.onSuccess?.();
    },
    onError: (error) => {
      options.onError?.(error);
    },
  });
}

/**
 * Mutation hook for updating an existing past-experience record.
 */
export function useUpdateExperience(employeeId: string, tenantId: string, options: ExperienceMutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { experienceId: string; data: UpdateEmployeeExperienceDto }>({
    mutationFn: ({ experienceId, data }) => updateEmployeeExperience(employeeId, experienceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId, tenantId) });
      options.onSuccess?.();
    },
    onError: (error) => {
      options.onError?.(error);
    },
  });
}

/**
 * Mutation hook for deleting a past-experience record.
 */
export function useDeleteExperience(employeeId: string, tenantId: string, options: ExperienceMutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { experienceId: string }>({
    mutationFn: ({ experienceId }) => deleteEmployeeExperience(employeeId, experienceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(employeeId, tenantId) });
      options.onSuccess?.();
    },
    onError: (error) => {
      options.onError?.(error);
    },
  });
}
